import { Box, Button, Typography, createStyles, makeStyles } from '@material-ui/core';
import cn from 'classnames';
import moment from 'moment';
import * as React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { TranquiTheme } from '../../../containers/App/theme';
import BudgetCategoryTypeTitle from '../../../containers/BudgetCategoryTypeList/BudgetCategoryTypeTitle';
import { useSpace } from '../../../content/utils/useSpace';
import MarkdownRender from '../../../dictionaries/MarkdownRender';
import RenderText from '../../../dictionaries/RenderText';
import useDictionary from '../../../dictionaries/context/useDictionary';
import { DebtType, NegotiationOption, NegotiationOptionAlternative, NegotiationOptionAlternativeType, Normative026, OutDebt, useGetOutDebtsQuery } from '../../../generated/graphql';
import { CssSpreadType } from '../../../utils/css-spread-type';
import { formatIRate } from '../../../utils/formatIRate';
import { getCommissionMultiplier } from '../../../utils/getCommissionMultiplier';
import { getCommissionPercentText } from '../../../utils/getCommissionPercentText';
import { getCustomContentText } from '../../../utils/getCustomContentText';
import { getDaysBehindLimitKeyRappi } from '../../../utils/getDaysBehindLimitKeyRappi';
import { getDiscountValue } from '../../../utils/getDiscountValue';
import { getExtraN26Suffix } from '../../../utils/getExtraN26Suffix';
import { getNegBooleans } from '../../../utils/getNegBooleans';
import { getNegotiationOptionLabel } from '../../../utils/getNegotiationOptionLabel';
import { getNormative026ContentKey } from '../../../utils/getNormative026ContentKey';
import { getWeightedRateText } from '../../../utils/getWeightedRateText';
import thousandSeparator from '../../../utils/thousandSeparator';
import AlertBase from '../../Alerts/AlertBase';
import Btn from '../../Btn/Btn';
import BenefitsAndConsequences from '../BenefitsAndConsequences';
import NewPaymentTermsContent from './NewPaymentTermsContent/NewPaymentTermsContent';
import { useGetGlobalSettingDomainByEntity } from '../../../utils/useGetGlobalSettingDomainByEntity';

interface NewPaymentTermsProps {
  negOption: NegotiationOption;
  alternative: NegotiationOptionAlternative | undefined;
  onConfirm?: () => void;
  className?: string;
  outDebt: OutDebt;
}

const NewPaymentTerms: React.FC<NewPaymentTermsProps> = (props) => {
  const { negOption, alternative, onConfirm, className, outDebt } = props;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const history = useHistory();
  const { gT } = useDictionary();

  const textContext = {
    productNumber: outDebt?.productNumber || '',
    paymentDay: moment(outDebt?.paymentDate).isValid() ? moment(outDebt?.paymentDate).format('D') : '',
    bank: outDebt?.bank,
  };
  const { data: outDebtsData } = useGetOutDebtsQuery();

  const isVirtual = outDebt.type === DebtType.Virtual;
  const parentDebt = outDebtsData?.outDebts.find(({ type, extra }) => {
    return type === DebtType.Virtual && extra?.children?.find((ch) => ch.id === outDebt.id);
  });
  const goToVirtualDebt = () => history.push(`/portal/deuda/${parentDebt?.id}`);
  const monthlyRate = negOption?.rate;

  const {
    isPartialPaymentPromise,
    isTotalPayment,
    isPaymentPromise,
    isMovistarMoney,
    isACPK,
    isRappiPay,
    isRappiPrestamo,
    isRestructure,
    isDav,
    isBogota,
    isRefinance,
    isBalanceBehindPayment,
    isBogotaNoPayment,
    isScotiabank,
    isCondonation,
    isQuotification,
    isPrepaymentCompleted,
    isApprovedByBank,
    isCatchUp,
    isMovistarMoneyInvoice,
    isBogotaLowRate,
  } = getNegBooleans(outDebt, negOption);

  const showPrepaymentAlertTexts = !!alternative?.prePayment
  && !isTotalPayment
  && !isPaymentPromise
  && !isPartialPaymentPromise;

  const discountPercent = !isBogota && !!alternative?.discountPercent
    && getDiscountValue(alternative.discountPercent);

  const validDiscountPercent = discountPercent && parseFloat(discountPercent) > 0;

  const n26Suffix = getExtraN26Suffix(outDebt, negOption);
  const rateRaw = negOption?.rate || 0;
  const rateStr = `${(rateRaw * 100).toFixed(2)}%`;

  const { daysBehind } = outDebt;
  const prePaymentBulletKey = (
    (!!alternative?.prePayment && isBogota && isRefinance)
    || (!!alternative?.prePayment && (isRappiPay || isRappiPrestamo)
      && (isRefinance || isQuotification))
  ) ? '_prepayment' : '';

  const getBlockedBulletKey = () => {
    // this is only relevant in BdB
    if (!isBogota) return '';
    if (isRefinance && !(isPrepaymentCompleted || isApprovedByBank)) return '';
    if (!!daysBehind && daysBehind < 60) { return '_no_blocked'; } if ((daysBehind || 0) > 60) return '_blocked';
    return '';
  };

  const fallenSubkey = outDebt?.extra?.isFallen ? '_fallen' : '';
  const movistarInvoice = isMovistarMoney && isCatchUp && isMovistarMoneyInvoice ? '_invoice' : '';
  const hasWeightedRateSubKey = isBogota && !!outDebt.extra?.maxTerm ? '_weighted_rate' : '';

  let bogotaNoPaymentTitleAndSubtitleKey = '';
  if (isBogota && isBogotaNoPayment) {
    bogotaNoPaymentTitleAndSubtitleKey = '_NO_PAYMENT';
  } else if (isBogotaLowRate) {
    bogotaNoPaymentTitleAndSubtitleKey = '_LOW_RATE';
  }

  const isCeded30 = isRappiPay
  && isCondonation && alternative?.type === NegotiationOptionAlternativeType.Condonation;
  const ceded30CondonationSubKey = isCeded30 ? '_ceded30Condonation' : '';

  const commissionValue = ((isRappiPay || isRappiPrestamo) && isRestructure)
    ? getCommissionMultiplier(outDebt) * (alternative?.value || 0)
    : (outDebt.extra?.commissionValue || 0) / (alternative?.term || 1);

  const { daysBehindLimitSubKey } = getDaysBehindLimitKeyRappi(outDebt, negOption);

  const alternativeTypeSubkey = isBogota && (isRefinance || isBalanceBehindPayment) && alternative?.type ? `_${alternative?.type}` : '';
  const negOptionSubKey = `${negOption.type}${alternativeTypeSubkey}`;
  const n026Key = getNormative026ContentKey(negOption, outDebt, true, alternative, outDebtsData?.outDebts);
  const consequencesKey = n026Key
    || `negotiation_${negOptionSubKey}_consequences${prePaymentBulletKey}${getBlockedBulletKey()}${fallenSubkey}${hasWeightedRateSubKey}${ceded30CondonationSubKey}${daysBehindLimitSubKey}${movistarInvoice}`;

  // space implementation
  const spaceContext = {
    outDebt,
    option: negOption,
    alternative,
    popupRate: outDebt?.extra?.popupRate || '',
    effectiveAnnualRate: ((alternative?.effectiveAnnualRate || 0) * 100).toFixed(2),
    discountPercent: ((alternative?.discountPercent || 0) * 100).toFixed(2),
  };
  const newPaymentTitle = useSpace('NEG_OPTION_TERMS', 'title', spaceContext);
  const benefitsTitle = useSpace('NEG_OPTION_CONDITIONS', 'benefits.title', spaceContext);
  const benefitsText = useSpace('NEG_OPTION_CONDITIONS', 'benefits.text', spaceContext);
  const consequencesText = useSpace('NEG_OPTION_CONDITIONS', 'consequences.text', spaceContext);

  const negType = getNegotiationOptionLabel(
    negOption.type,
    outDebt.bank,
    outDebt.extra?.normative026 as Normative026,
    alternative,
    outDebt.brand,
  );

  const domainWhatsapp = useGetGlobalSettingDomainByEntity('domainWhatsapp');
  const domainCalls = useGetGlobalSettingDomainByEntity('domainCalls');
  const phoneNumber = domainWhatsapp?.formattedNumber || domainWhatsapp?.number || '';
  const callPhoneNumber = domainCalls?.formattedNumber || domainCalls?.number || '';

  return (
    <>
      {discountPercent && validDiscountPercent
      && (
      <Box className={cn(classes.discountBox)}>
        {discountPercent}% DE DESCUENTO
      </Box>
      )}
      <Box
        className={cn(classes.generalContent, isVirtual && classes.generalContentACPK, className)}
        data-testid={`new-payment-terms__box-${negOption.type}`}
      >
        <BudgetCategoryTypeTitle
          className={cn('mb-1', classes.customTermsBox)}
          title={`${gT(`newPaymentTerms_subtitle${bogotaNoPaymentTitleAndSubtitleKey}`)}`}
          subtitle={newPaymentTitle.text!}
          classNameTitle={classes.customTitleNewTerm}
          classNameSubTitle={classes.customSubtitleNewTerm}
        />
        <NewPaymentTermsContent
          outDebt={outDebt}
          negOption={negOption}
          alternative={alternative!}
          parentDebt={(parentDebt as OutDebt)}
        />

        {onConfirm && (
          <>
            <Box className={cn(classes.btnWrapper, 'mt-2')}>
              <Btn
                className={classes.confirmBtn}
                fullWidth
                variant="contained"
                color="primary"
                data-testid={`new-payment-terms__${negOption.type}-${alternative?.term}`}
                onClick={isACPK && !isVirtual && !!parentDebt ? goToVirtualDebt : onConfirm}
              >
                <Typography noWrap className={classes.newPaymentTermsBtnText}>
                  Solicitar
                  {' '}
                  <RenderText isMarkdown textKey={`negotiation_${negOption.type}${n26Suffix}_btn_label`} />
                </Typography>
              </Btn>
            </Box>
            {isVirtual && (
            <Box className={cn(classes.btnWrapper)}>
              <Button
                className={classes.ACPKBtnGoBack}
                component={Link}
                to="/portal/deudas"
                variant="outlined"
                color="secondary"
                data-test={`newPaymentTermsBtn-selected-${negOption.type}-moreDebts`}
              >
                Ver todas mis deudas
              </Button>
            </Box>
            )}
          </>
        )}
        {showPrepaymentAlertTexts && (
          <AlertBase type="informative" title={(isMovistarMoney && isACPK) ? 'Importante' : 'Sobre el abono inicial'} className="my-2" filled={false}>
            <MarkdownRender text={gT('negotiation_prePayment_alert', textContext)} />
          </AlertBase>
        )}

        {!(isPartialPaymentPromise && (isDav || isScotiabank)) && (
        <Box className={classes.detailsBox}>
          <BenefitsAndConsequences
            defaultExpanded={!!bogotaNoPaymentTitleAndSubtitleKey}
            titleKey={benefitsTitle.keyIf || `negotiation_${negOption.type}_benefitsTitle`}
            contentKey={benefitsText.keyIf || `negotiation_${negOption.type}_benefits`}
            context={{
              value: String(alternative?.discountValue),
              hidden: (alternative?.discountValue || 0) <= 0,
              ...benefitsText.context,
            }}
            isBenefits
          />
          <BenefitsAndConsequences
            dataTestid="new-payment-terms__consequences"
            titleKey={`negotiation_${negOption.type}_consequencesTitle`}
            contentKey={consequencesText.keyIf || consequencesKey}
            context={{
              rate: rateStr,
              paymentDay: moment(outDebt.paymentDate).date(),
              monthlyRate: formatIRate(monthlyRate, 'm'),
              annualRate: formatIRate(monthlyRate, 'y'),
              billingDay: outDebt.extra?.billingDay || '-',
              commissionValue: thousandSeparator(commissionValue),
              headProductNumber: outDebt.extra?.headProductNumber || '',
              commissionPercent: `${((alternative?.commissionPercent || 0) * 100).toFixed(2)}%`,
              commissionText: getCommissionPercentText(gT, 'commissionAuxiliarText', alternative?.commissionPercent || 0),
              commissionPercentBulletText: getCommissionPercentText(gT, 'commissionPercentBulletText', alternative?.commissionPercent || 0),
              weightedRateText: getWeightedRateText(gT, outDebt.extra?.popupRaw || ''),
              negType: negOption.type,
              negTypeLabel: negType,
              issuingEntity: outDebt.extra?.issuingEntity || '',
              otherChargesNonDue: thousandSeparator(outDebt?.extra?.otherChargesNonDue || 0),
              alternative: alternative!,
              ...consequencesText.context,
              phoneNumber,
              callPhoneNumber,
            }}
            defaultExpanded={!!bogotaNoPaymentTitleAndSubtitleKey}
            contentText={
              consequencesText.exists ? null : getCustomContentText(
                {
                  gT,
                  negOption,
                  outDebt,
                  outDebts: outDebtsData?.outDebts,
                  alternative,
                  isConsequences: true,
                },
              )
            }
          />
        </Box>
        )}
      </Box>
    </>
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  detailsBox: {
    display: 'grid',
    marginBlockStart: theme.spacing(2),
  },
  ACPKBtnGoBack: {
    color: theme.palette.lavender.main,
    textDecoration: 'underline',
    marginTop: theme.spacing(2),
    textAlign: 'center',
    fontWeight: 400,
  },
  confirmBtn: {
    fontWeight: 'normal',
    letterSpacing: '1.5px',
    [theme.breakpoints.up('md')]: {
      maxInlineSize: '300px',
    },
  },
  generalContent: {
    backgroundColor: theme.palette.pearl.main,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(2),
    boxSizing: 'border-box',
  },
  discountBox: {
    backgroundColor: theme.palette.primary.light,
    borderRadius: theme.spacing(0.5, 0.5, 0, 0),
    padding: theme.spacing(2),
    boxSizing: 'border-box',
    textAlign: 'center',
    fontSize: '1rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  generalContentACPK: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      backgroundColor: theme.palette.marble.main,
      boxShadow: theme.shadows[0],
      padding: theme.spacing(1),
    },
  },
  customTermsBox: {
    display: 'flex',
    flexDirection: 'column-reverse',
  },
  customTitleNewTerm: {
    maxWidth: 'none',
    marginBottom: 0,
    ...theme.typography.body1,
    fontWeight: 600,
    color: theme.palette.indigo.main,
  },
  customSubtitleNewTerm: {
    ...(theme.typography.small as CssSpreadType),
    color: theme.palette.text4.main,
    marginBlockEnd: theme.spacing(0),
  },
  btnWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  newPaymentTermsBtnText: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.pearl.main,
  },
}));

export default NewPaymentTerms;
